import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useBusiness } from "@/context/BusinessContext";
import { Button } from "@/components/ui";

const STEPS = [
  { key: "business", label: "Business", path: "/onboarding/business", flag: "profileComplete" },
  { key: "knowledge", label: "Knowledge", path: "/onboarding/knowledge", flag: "knowledgeComplete" },
  { key: "integrations", label: "Integrations", path: "/onboarding/integrations", flag: "integrationsComplete" },
  { key: "playbook", label: "Skills", path: "/onboarding/playbook", flag: "playbookPublished" },
  { key: "whatsapp", label: "WhatsApp", path: "/onboarding/whatsapp", flag: "whatsappActive" },
];

const isTyping = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
};

function ActionButton({ action }) {
  if (!action) return null;
  const { label, onClick, loading, disabled, testId, icon, variant = "accent2" } = action;
  return (
    <Button variant={variant} onClick={onClick} disabled={disabled || loading} data-testid={testId}>
      {loading ? (label?.loading || "Working…") : (<>{label}{icon}</>)}
    </Button>
  );
}

export default function OnboardingLayout({ current, title, sidebar, children, primaryAction, secondaryAction }) {
  const nav = useNavigate();
  const { active } = useBusiness();
  const idx = STEPS.findIndex((s) => s.key === current);
  const prev = idx > 0 ? STEPS[idx - 1] : null;
  const next = idx < STEPS.length - 1 ? STEPS[idx + 1] : null;

  React.useEffect(() => {
    const onKey = (e) => {
      if (isTyping() || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowRight" && next) nav(next.path);
      else if (e.key === "ArrowLeft" && prev) nav(prev.path);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav, next, prev]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--canvas)" }}>
      <header className="flex items-center justify-between px-5 md:px-10 h-14 flex-shrink-0">
        <button onClick={() => nav("/app")} className="flex items-center gap-1.5 text-sm" style={{ color: "var(--text-2)" }} data-testid="back-to-workspaces">
          <ChevronLeft size={16} /> Workspaces
        </button>
        <Link to="/" className="flex items-center gap-2.5" data-testid="onboarding-logo" title="Home">
          <div className="brand-mark" style={{ width: 16, height: 16 }} />
          <span className="mono font-semibold text-sm">Wappy</span>
          {active && <span className="mono text-xs ml-1 truncate max-w-[140px]" style={{ color: "var(--text-muted)" }}>/ {active.name}</span>}
        </Link>
      </header>

      <nav className="flex-shrink-0">
        <div className="max-w-5xl mx-auto px-5 md:px-10 flex items-center justify-start md:justify-between gap-6 overflow-x-auto no-scrollbar pt-1">
          {STEPS.map((s, i) => {
            const done = !!active?.onboarding?.[s.flag], isActive = i === idx;
            return (
              <button key={s.key} onClick={() => nav(s.path)}
                className={`step-item ${isActive ? "is-active" : ""} ${done ? "is-done" : ""}`}
                data-testid={`setup-tab-${s.key}`} aria-current={isActive ? "step" : undefined}>
                <span className={`step-badge ${isActive ? "is-active" : ""} ${done ? "is-done" : ""}`}>
                  {done ? <Check size={12} /> : i + 1}
                </span>
                {s.label}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="flex-1 w-full max-w-5xl mx-auto px-5 md:px-10 flex min-w-0 pb-24">
        {sidebar && (
          <aside className="onb onb-rail hidden md:block w-52 flex-shrink-0 md:overflow-y-auto md:pr-7" data-testid="setup-rail">
            <div className="py-6">{sidebar}</div>
          </aside>
        )}
        <main className={`onb flex-1 min-w-0 pt-8 pb-28 ${sidebar ? "md:pl-8" : ""}`}>
          {children}
        </main>
      </div>

      {(primaryAction || secondaryAction || prev || next) && (
        <footer className="fixed bottom-0 left-0 w-full z-40" data-testid="setup-footer"
          style={{ borderTop: "1px solid var(--border)", background: "rgba(9,11,14,0.82)", backdropFilter: "blur(14px)" }}>
          <div className="max-w-5xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ActionButton action={secondaryAction} />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => prev && nav(prev.path)} disabled={!prev} data-testid="setup-footer-prev"
                className="btn btn-ghost btn-sm" title="Previous (←)"><ChevronLeft size={16} /></button>
              <button onClick={() => next && nav(next.path)} disabled={!next} data-testid="setup-footer-next"
                className="btn btn-ghost btn-sm" title="Next (→)"><ChevronRight size={16} /></button>
              <ActionButton action={primaryAction} />
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
